// Word completion for English writing: the rest of the word being typed, from a list of English
// words ordered by how much they're used, and the words that look mistyped, with those they were
// likely meant to be. It all happens in the browser, with a list that's downloaded the first time
// it's needed.

/** Letters to type before a word is completed, or taken for a typo. */
const MIN_TYPED = 3;
/** Words offered in place of a typo. */
const MAX_FIXES = 5;
/** How much of the end of the text is read, to tell whether it's English. */
const RECENT = 600;
/**
 * Whole words of MIN_TYPED letters or more from which a share of English ones is enough to tell,
 * and that share. Fewer words must all be English.
 */
const MIN_WORDS = 3;
const ENGLISH_SHARE = 0.6;

export interface WordList {
  /** Each word's place in the list: 0 for the most used. */
  readonly rank: ReadonlyMap<string, number>;
  /** Words longer than MIN_TYPED letters, by their first MIN_TYPED letters, most used first. */
  readonly byStart: ReadonlyMap<string, readonly string[]>;
}

/** Reads english-words.txt: a word a line, most used first. Lines starting with # are notes. */
export function parseWordList(text: string): WordList {
  const rank = new Map<string, number>();
  const byStart = new Map<string, string[]>();
  for (const word of text.split('\n')) {
    if (!word || word.startsWith('#')) continue;
    rank.set(word, rank.size);
    if (word.length <= MIN_TYPED) continue;
    const start = word.slice(0, MIN_TYPED);
    const words = byStart.get(start);
    if (words) words.push(word);
    else byStart.set(start, [word]);
  }
  return { rank, byStart };
}

export interface Completion {
  /** Where the letters typed so far start. */
  readonly start: number;
  /** Where they end: the caret. */
  readonly end: number;
  /** The rest of the word, in capitals if that's how it's being typed. */
  readonly rest: string;
}

// The letters just before the caret, when they begin a word: not after another letter, a digit,
// or an apostrophe, and in the plain alphabet English is written in.
const TYPED_WORD = /(?:^|[^\p{L}\p{M}\p{N}_'’])([A-Za-z]+)$/u;

const isCapitals = (letters: string) => letters.length > 1 && letters === letters.toUpperCase();

export interface Completions {
  /** Where the letters typed so far start. */
  readonly start: number;
  /** Where they end: the caret. */
  readonly end: number;
  /**
   * Words that start with those letters, most used first, beginning as they were typed. For a
   * typo, the words to put in their place instead, likeliest first.
   */
  readonly words: readonly string[];
  /**
   * Whether the first is likely the word being typed. It isn't when the letters are already a
   * word used at least as much, such as "work" before "working".
   */
  readonly likely: boolean;
  /**
   * Whether the letters look mistyped: no word starts with them, but some do with one slip put
   * right, such as "recieve" for "receive".
   */
  readonly typo: boolean;
}

/**
 * Up to `limit` words that start with the letters the caret is at the end of, or when none do and
 * those letters look mistyped, the words they were likely meant to be.
 */
export function completionsAt(
  list: WordList,
  text: string,
  caret: number,
  limit: number,
): Completions | null {
  // Only where nothing follows on the line, so the rest of a word never covers other text.
  const after = text.charAt(caret);
  if (after !== '' && after !== '\n' && after !== '\r') return null;
  const typed = TYPED_WORD.exec(text.slice(Math.max(0, caret - 64), caret))?.[1];
  if (!typed || typed.length < MIN_TYPED) return null;

  const start = caret - typed.length;
  const lower = typed.toLowerCase();
  const typedRank = list.rank.get(lower) ?? Infinity;
  const words: string[] = [];
  let likely = false;
  for (const word of list.byStart.get(lower.slice(0, MIN_TYPED)) ?? []) {
    if (word.length <= lower.length || !word.startsWith(lower)) continue;
    if (words.length === 0) likely = (list.rank.get(word) ?? Infinity) < typedRank;
    const rest = word.slice(lower.length);
    words.push(typed + (isCapitals(typed) ? rest.toUpperCase() : rest));
    if (words.length === limit) break;
  }
  if (words.length > 0) return { start, end: caret, words, likely, typo: false };

  // Nothing starts with the letters: they may be mistyped, for a word that's whole or not yet.
  if (typedRank !== Infinity || !mayBeTypo(text, start, typed)) return null;
  const fixes = oneSlipFrom(list, lower)
    .slice(0, limit)
    .map(({ word }) => (typed === lower ? word : capitalize(word)));
  return fixes.length > 0 ? { start, end: caret, words: fixes, likely: true, typo: true } : null;
}

export interface Typo {
  /** Where the word starts. */
  readonly start: number;
  /** Where it ends. */
  readonly end: number;
  /** The words it was likely meant to be, likeliest first, with its capital if it has one. */
  readonly fixes: readonly string[];
}

// Words in plain letters, not joined to other letters, digits, or apostrophes.
const WORD = /(?<![\p{L}\p{M}\p{N}_'’])[A-Za-z]+(?![\p{L}\p{M}\p{N}_'’])/gu;
// Links, email addresses, and paths, whose words aren't prose.
const NOT_PROSE = /[@/\\]|\.\p{L}/u;

/**
 * The words in the text that look mistyped: not in the list, but one slip from a whole word that
 * is, such as "recieve". Names, abbreviations, links, and addresses are left alone.
 */
export function typosIn(list: WordList, text: string): Typo[] {
  const typos: Typo[] = [];
  for (const match of text.matchAll(WORD)) {
    const word = match[0];
    const lower = word.toLowerCase();
    if (word.length < MIN_TYPED || list.rank.has(lower)) continue;
    const fixes = wholeFixes(list, lower);
    const start = match.index;
    const end = start + word.length;
    if (fixes.length === 0 || !mayBeTypo(text, start, word)) continue;
    if (NOT_PROSE.test(chunkAround(text, start, end))) continue;
    typos.push({ start, end, fixes: word === lower ? fixes : fixes.map(capitalize) });
  }
  return typos;
}

/** The text around a word as far as whitespace on either side, such as the whole of a link. */
function chunkAround(text: string, start: number, end: number): string {
  const before = /\S*$/.exec(text.slice(Math.max(0, start - 200), start))?.[0] ?? '';
  const after = /^\S*/.exec(text.slice(end, end + 200))?.[0] ?? '';
  return before + text.slice(start, end) + after;
}

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

// Where a sentence begins: at the start of the text or of a line, or after the end of another,
// with any opening quotes, brackets, or list markers between.
const SENTENCE_START = /(?:^|\n|[.!?…]['"’”)\]]*\s)[\s'"‘“([*•-]*$/u;

/**
 * Whether letters the list doesn't have could be a typo, rather than a name or an abbreviation:
 * those have capitals after the first letter (NASA, iPhone), or a capital within a sentence.
 */
function mayBeTypo(text: string, start: number, typed: string): boolean {
  if (/[A-Z]/.test(typed.slice(1))) return false;
  return !/[A-Z]/.test(typed.charAt(0)) || SENTENCE_START.test(text.slice(0, start));
}

/**
 * The words that start with the letters typed (in lower case) but for one slip, likeliest first,
 * each with its order from slipOrder: whole words before longer ones; among whole words, two
 * letters swapped, then one left out, then one too many or one wrong; and then the most used.
 * Three letters are only taken for a whole word with two of them swapped, such as "teh" for
 * "the": any other slip in so few letters could make too many words.
 */
function oneSlipFrom(list: WordList, typed: string): { word: string; order: number }[] {
  const found: { word: string; order: number }[] = [];
  // Most used first: sorting keeps that order among equals.
  for (const word of list.rank.keys()) {
    if (word.length < MIN_TYPED) continue;
    const order = slipOrder(typed, word);
    if (order === null || (typed.length === MIN_TYPED && order > 0)) continue;
    found.push({ word, order });
  }
  return found.sort((a, b) => a.order - b.order);
}

// The slip order of a word that only starts with the letters, put right: it's longer than them.
const ONLY_STARTS = 3;

/**
 * How `word` starts with the letters typed but for one slip: 0 when that makes the whole word by
 * swapping two letters, 1 by putting in one left out, 2 by taking out one too many or putting
 * one wrong right, and ONLY_STARTS when the word only starts that way. Null if one slip won't do,
 * or none is needed.
 */
function slipOrder(typed: string, word: string): number | null {
  // The first letter is taken to be right, but for a swap with the second: slips there are rare,
  // and could make any word.
  const first = word.charAt(0);
  if (first !== typed.charAt(0) && first !== typed.charAt(1)) return null;
  let at = 0;
  while (at < typed.length && typed[at] === word[at]) at++;
  if (at === typed.length) return null;

  const head = typed.slice(0, at);
  const letter = word.charAt(at);
  const fixes: [fix: string, order: number][] = [];
  if (at + 1 < typed.length) {
    fixes.push([head + typed.charAt(at + 1) + typed.charAt(at) + typed.slice(at + 2), 0]);
  }
  if (at > 0 && letter) {
    fixes.push([head + letter + typed.slice(at), 1], [head + letter + typed.slice(at + 1), 2]);
  }
  if (at > 0) fixes.push([head + typed.slice(at + 1), 2]);

  let best: number | null = null;
  for (const [fix, order] of fixes) {
    if (!word.startsWith(fix)) continue;
    const found = word.length === fix.length ? order : ONLY_STARTS;
    if (best === null || found < best) best = found;
  }
  return best;
}

/**
 * The rest of the word the caret is at the end of: the most used word that starts with the
 * letters typed, unless those letters are already a word that's used at least as much. A typo
 * has no rest.
 */
export function completionAt(list: WordList, text: string, caret: number): Completion | null {
  const found = completionsAt(list, text, caret, 1);
  const word = found?.likely && !found.typo ? found.words[0] : undefined;
  if (!found || word === undefined) return null;
  return { start: found.start, end: found.end, rest: word.slice(found.end - found.start) };
}

/** Whether the text could be English, going by its alphabet: worth loading the list to check. */
export function mightBeEnglish(text: string): boolean {
  const recent = text.slice(-RECENT);
  const letters = recent.match(/\p{L}/gu)?.length ?? 0;
  const plain = recent.match(/[A-Za-z]/g)?.length ?? 0;
  return letters > 0 && plain >= letters * 0.9;
}

/**
 * Whether the end of the text reads as English, going by how many of its recent whole words are
 * in the list. Null until there's a whole word to go by.
 */
export function readsAsEnglish(list: WordList, text: string): boolean | null {
  // Whole words only: the last one may still be being typed.
  const words = [
    ...text
      .slice(-RECENT)
      .toLowerCase()
      .matchAll(/[\p{L}\p{M}]+(?=[^\p{L}\p{M}])/gu),
  ]
    .map(([word]) => word)
    .filter((word) => word.length >= MIN_TYPED);
  const english = words.filter((word) => list.rank.has(word)).length;
  // The others count against English, but for any one slip from a word in the list: they may be
  // typos of it, and count neither way. Once English can't have most, that's that.
  let others = 0;
  for (const word of words) {
    if (list.rank.has(word) || wholeFixes(list, word).length > 0) continue;
    others += 1;
    if (english < (english + others) * ENGLISH_SHARE) return false;
  }
  if (english + others === 0) return null;
  // A word or two decide only if they're all English; after that, most must be.
  return english + others < MIN_WORDS
    ? others === 0
    : english >= (english + others) * ENGLISH_SHARE;
}

// For each list, the words it doesn't have that have been looked up, with their fixes.
const fixesFound = new WeakMap<WordList, Map<string, readonly string[]>>();

/** The whole words one slip from a word the list doesn't have, in lower case, likeliest first. */
function wholeFixes(list: WordList, word: string): readonly string[] {
  let known = fixesFound.get(list);
  if (!known) {
    known = new Map();
    fixesFound.set(list, known);
  }
  let fixes = known.get(word);
  if (!fixes) {
    fixes = oneSlipFrom(list, word)
      .filter(({ order }) => order < ONLY_STARTS)
      .slice(0, MAX_FIXES)
      .map((fix) => fix.word);
    known.set(word, fixes);
  }
  return fixes;
}

let loading: Promise<WordList> | undefined;
let loaded: WordList | undefined;

/** The list of English words, downloaded once, the first time it's needed. */
export function loadEnglishWords(): Promise<WordList> {
  loading ??= import('./english-words.txt?raw').then(
    ({ default: text }) => {
      loaded = parseWordList(text);
      return loaded;
    },
    (error: unknown) => {
      // Try again next time.
      loading = undefined;
      throw error;
    },
  );
  return loading;
}

/** The list of English words, once it has downloaded; null until then. */
export function loadedEnglishWords(): WordList | null {
  return loaded ?? null;
}
